import Head from 'next/head'
import React from 'react'
import type { SubmitHandler } from 'react-hook-form'
import { Controller, useForm } from 'react-hook-form'

import { useAnalytics } from '@src/Analytics'
import useClient from '@src/api/useClient'
import Button from '@src/components/ui/Button'
import LoadingButton from '@src/components/ui/LoadingButton'
import TextField from '@src/components/ui/TextField'
import useRouter from '@src/hooks/useRouter'
import BaseLayout from '@src/layouts/BaseLayout'
import * as validation from '@src/lib/validation'
import withDefaults from '@src/server/props/withDefaults'
import useUserClient from '@src/supabase/useUserClient'

export const getServerSideProps = withDefaults()

interface FormData {
  email: string
  password: string
}

const LoginPage = (): JSX.Element => {
  const supabase = useUserClient()
  const client = useClient()
  const { track } = useAnalytics()
  const router = useRouter()

  const {
    control,
    formState: { errors, isSubmitting, isSubmitSuccessful },
    getValues,
    setError,
    handleSubmit: createSubmitHandler,
  } = useForm<FormData>({
    defaultValues: {
      email: (router.query.email as string) ?? '',
      password: '',
    },
  })

  const onSubmit: SubmitHandler<FormData> = async ({ email, password }) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    if (error) {
      const { exists, isConfirmed } = await client.get({
        endpoint: '/users/[email]/metadata',
        params: { email },
      })

      if (exists && !isConfirmed) {
        await router.push(
          `/auth/login/reconfirm?email=${encodeURIComponent(email)}`,
          '/auth/login/reconfirm',
        )
      } else {
        setError('email', { type: 'server', message: error.message })
        setError('password', { type: 'server', message: error.message })
      }
    } else {
      track('User logged in with password', { email })
      await router.push('/dashboard')
    }
  }

  const handleSubmit = createSubmitHandler(onSubmit)

  const handleSignupClicked = async () => {
    const { email } = getValues()
    await router.push(
      `/auth/signup?email=${encodeURIComponent(email)}`,
      '/auth/signup',
    )
  }

  const handleResetClicked = async () => {
    const { email } = getValues()
    await router.push(
      `/auth/reset?email=${encodeURIComponent(email)}`,
      '/auth/reset',
    )
  }

  return (
    <>
      <Head>
        <title>Login | MateAI</title>
      </Head>
      <div className="flex grow items-center justify-center">
        <div className="w-96">
          <form
            className="flex flex-col items-center"
            onSubmit={event => {
              void handleSubmit(event)
            }}
          >
            <h2 className="my-2 block text-center text-2xl font-semibold">
              Login
            </h2>
            <p className="my-2 text-center">
              Enter your email address and password to log back in to your
              account.
            </p>
            <Controller
              name="email"
              control={control}
              rules={{
                required: 'Email address is required',
                validate: validation.email,
              }}
              render={({ field, fieldState }) => (
                <TextField
                  autoFocus
                  label="Email"
                  errorMessage={fieldState.error?.message}
                  className="my-2 w-full"
                  {...field}
                />
              )}
            />
            <Controller
              name="password"
              control={control}
              rules={{ required: 'Password is required' }}
              render={({ field, fieldState }) => (
                <TextField
                  label="Password"
                  type="password"
                  autoComplete="current-password"
                  errorMessage={fieldState.error?.message}
                  className="my-2 w-full"
                  {...field}
                />
              )}
            />
            {errors.email?.type === 'server' &&
            errors.password?.type === 'server' ? (
              <p role="alert" className="my-4 text-center text-danger">
                Incorrect email or password.
              </p>
            ) : null}
            <LoadingButton
              type="submit"
              isLoading={isSubmitting || isSubmitSuccessful}
              className="mb-2 mt-8 w-full"
            >
              Login
            </LoadingButton>
            <Button
              variant="ghost"
              className="my-2 w-full"
              onPress={() => {
                void handleSignupClicked()
              }}
            >
              Don&apos;t have an account?
            </Button>
            <Button
              variant="ghost"
              className="my-2 w-full"
              onPress={() => {
                void handleResetClicked()
              }}
            >
              Forgot your password?
            </Button>
          </form>
        </div>
      </div>
    </>
  )
}

LoginPage.Layout = BaseLayout

export default LoginPage
